/**
 * Generic params for Pages scm:button type="page" → pages-config-execute.
 * Behavior is driven by markup attributes + pages variable mappings (not hardcoded flows).
 *
 * Plan C: UI does not merge form+rate into shipment_array. Backend
 * SelectedRateShipmentArrayMerge owns that when selected_rate is mapped.
 */

import {
  extractFieldValue,
  getDataScmAttributes,
} from "../../Components/MarkupConversion/MarkupFunctions";

/** True when Package rows still carry SBR description / cf_packages (not Get Rates dims-only). */
const packageRowsAreRich = (rows) => {
  if (!Array.isArray(rows)) {
    return false;
  }
  return rows.some((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return false;
    }
    const pkg = row.cf_packages;
    if (pkg != null && typeof pkg === "object" && !Array.isArray(pkg)) {
      return true;
    }
    const description =
      row.cf_description ?? row.description ?? row.Description;
    return description != null && String(description).trim() !== "";
  });
};

/** RATINGS execute may persist Package as a JSON string; coerce back to rows. */
const coercePackageRows = (raw) => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw !== "string") {
    return raw;
  }
  const trimmed = raw.trim();
  if (!trimmed.startsWith("[")) {
    return raw;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : raw;
  } catch (e) {
    return raw;
  }
};

/** True when at least one row already has weight or L/W/H from the live form. */
const packageRowsHaveFormDims = (rows) => {
  const list = coercePackageRows(rows);
  if (!Array.isArray(list)) {
    return false;
  }
  return list.some((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return false;
    }
    return ["weight", "length", "width", "height"].some((key) => {
      const raw = row[key];
      return raw != null && String(raw).trim() !== "";
    });
  });
};

const additionalServicesNonEmpty = (val) =>
  Array.isArray(val) && val.length > 0;

/**
 * Upgrade slim Package / empty additional_services from a richer layer.
 * Never downgrades rich → slim or non-empty → [].
 * After Get Rates, host keeps dims-only Package; remount may put blank
 * [{weight:""}] in formFlat and overwrite — restore host dims in that case.
 */
const preferRichPackageAndServices = (target, layer = {}) => {
  if (!target || !layer || typeof layer !== "object" || Array.isArray(layer)) {
    return;
  }
  ["Package", "package", "Package_out", "package_out"].forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(layer, key)) {
      return;
    }
    const layerRows = coercePackageRows(layer[key]);
    const targetRows = coercePackageRows(target[key]);
    if (packageRowsAreRich(layerRows) && !packageRowsAreRich(targetRows)) {
      if (packageRowsHaveFormDims(targetRows)) {
        return;
      }
      target[key] = layerRows;
      return;
    }
    // Dims-only host/Get Rates rows are not "rich"; still beat blank remount rows.
    if (packageRowsHaveFormDims(layerRows) && !packageRowsHaveFormDims(targetRows)) {
      target[key] = layerRows;
    }
  });
  [
    "from_additional_services",
    "to_additional_services",
    "Ship From Additional Services",
    "Ship To Additional Services",
  ].forEach((key) => {
    if (
      additionalServicesNonEmpty(layer[key]) &&
      !additionalServicesNonEmpty(target[key])
    ) {
      target[key] = layer[key];
    }
  });
};

const unwrapPagesContextValue = (raw) => {
  if (raw == null) {
    return raw;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    if (Object.prototype.hasOwnProperty.call(raw, "value")) {
      return raw.value;
    }
    if (Object.prototype.hasOwnProperty.call(raw, "selected_value")) {
      return raw.selected_value;
    }
  }
  return raw;
};

const readScmBooleanAttr = (attributes, keys, defaultValue = true) => {
  if (!attributes || typeof attributes !== "object") {
    return defaultValue;
  }
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (!Object.prototype.hasOwnProperty.call(attributes, key)) {
      continue;
    }
    const raw = attributes[key];
    const norm = String(raw).trim().toLowerCase();
    if (norm === "false" || norm === "0" || norm === "off" || norm === "no") {
      return false;
    }
    if (norm === "true" || norm === "1" || norm === "on" || norm === "yes") {
      return true;
    }
  }
  return defaultValue;
};

/** Default true: buttons inside scm:dataTable loops send the row into execute params. */
export const shouldMergeLoopIntoPageAutomationParams = (attributes) =>
  readScmBooleanAttr(
    attributes,
    ["mergeLoop", "mergeloop", "loopContext", "loopcontext"],
    true
  );

/** Default true: merge inputParams / outputResponse / form field payload into execute params. */
export const shouldCarryForwardPageContext = (attributes) =>
  readScmBooleanAttr(
    attributes,
    ["carryForward", "carryforward", "pageContext", "pagecontext"],
    true
  );

/**
 * Markup data-scm-selected_rate (or selected_rate): pass clicked loop row as
 * selected_rate only — do not spread the card over form fields.
 */
export const shouldPassSelectedRateFromLoop = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object") {
    return false;
  }
  return (
    Object.prototype.hasOwnProperty.call(attributes, "data-scm-selected_rate") ||
    Object.prototype.hasOwnProperty.call(attributes, "selected_rate") ||
    Object.prototype.hasOwnProperty.call(attributes, "dataScmSelected_rate") ||
    Object.prototype.hasOwnProperty.call(attributes, "dataScmSelectedRate")
  );
};

/** Rate-card shape from Get Rates scm:dataTable (Plan C fallback when attr missing). */
export const looksLikeSelectedRateCard = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return false;
  }
  const hasCarrier =
    row.carrier_name != null ||
    row.carrier_id != null ||
    row.carrierId != null ||
    row["Carrier Name"] != null;
  const hasRate =
    row.final_rate != null ||
    row.customer_rate != null ||
    row["Final Rate"] != null ||
    row["Customer Rate"] != null;
  return Boolean(hasCarrier && hasRate);
};

const isUnresolvedSelectedRatePlaceholder = (value) => {
  if (value == null || value === "") {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  return (
    trimmed === "" ||
    trimmed.includes("${") ||
    trimmed.includes("{{") ||
    trimmed.toLowerCase() === "null" ||
    trimmed.toLowerCase() === "undefined"
  );
};

/** ISO-2 country mistaken for postal (same guard as Get Rates / Plan C builder). */
export const isLikelyCountryCodeNotPostal = (val) =>
  val != null && /^[A-Za-z]{2}$/.test(String(val).trim());

export const isPagesPostalVariableKey = (key) => {
  const k = String(key || "").toLowerCase();
  return (
    k === "from_postal_code" ||
    k === "to_postal_code" ||
    k === "from_postalcode" ||
    k === "to_postalcode" ||
    k === "from_postal_/_zip_code" ||
    k === "to_postal_/_zip_code" ||
    k.endsWith("postal_code") ||
    k.includes("postal_/_zip")
  );
};

const flattenPagesExecuteContext = (source) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }
  const out = {};
  Object.entries(source).forEach(([key, raw]) => {
    const val = unwrapPagesContextValue(raw);
    if (val === null || val === undefined || val === "") {
      return;
    }
    out[key] = val;
    const lower = String(key).toLowerCase();
    out[lower] = val;
  });
  return out;
};

const flattenFormPayload = (formPayload = {}) => {
  if (!formPayload || typeof formPayload !== "object") {
    return {};
  }
  const out = {};
  Object.entries(formPayload).forEach(([key, entry]) => {
    const val = unwrapPagesContextValue(entry);
    if (val === null || val === undefined || val === "") {
      return;
    }
    out[key] = val;
    out[String(key).toLowerCase()] = val;
  });
  return out;
};

export const flattenFormPayloadForPagesExecute = flattenFormPayload;

const spreadShipObjContext = (source = {}) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }
  const row =
    (Array.isArray(source.ship_obj) ? source.ship_obj[0] : source.ship_obj) ||
    (Array.isArray(source.shipObj) ? source.shipObj[0] : source.shipObj);
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return {};
  }
  return flattenPagesExecuteContext(row);
};

/**
 * Host page (e.g. Add Shipment) params/response — rich Package often lives here
 * while the rates child form only has slim display rows. Generic carry-forward aid.
 */
const collectHostPageExecuteContext = (pageFlows) => {
  if (!pageFlows || typeof pageFlows !== "object") {
    return {};
  }
  const data = pageFlows.pageData;
  if (!data || typeof data !== "object") {
    return {};
  }
  const activeKey = pageFlows.activePage || pageFlows.activeWindowPage;
  let hostPages = null;
  if (activeKey && data[activeKey]?.pages?.html) {
    hostPages = data[activeKey].pages;
  } else {
    const hostKey = Object.keys(data).find((key) => data[key]?.pages?.html);
    if (hostKey) {
      hostPages = data[hostKey].pages;
    }
  }
  if (!hostPages) {
    return {};
  }
  return {
    ...flattenPagesExecuteContext(hostPages.params),
    ...spreadShipObjContext(hostPages.params),
    ...flattenPagesExecuteContext(hostPages.response),
    ...spreadShipObjContext(hostPages.response),
  };
};

/** Merge host page params/response + form state for pages-config-execute. */
export const buildPagesHostExecuteContext = ({
  inputParams = {},
  outputResponse = {},
  fieldFormPayload = {},
  hostPageParams = {},
  hostPageResponse = {},
}) => {
  const mergedResponse = {
    ...flattenPagesExecuteContext(hostPageResponse),
    ...flattenPagesExecuteContext(outputResponse),
  };
  const hostFlat = {
    ...flattenPagesExecuteContext(hostPageParams),
    ...spreadShipObjContext(hostPageParams),
    ...mergedResponse,
    ...spreadShipObjContext(mergedResponse),
  };
  const formFlat = flattenFormPayload(fieldFormPayload);
  const merged = {
    ...hostFlat,
    ...flattenPagesExecuteContext(inputParams),
    ...spreadShipObjContext(inputParams),
    ...formFlat,
  };
  // Form/Get Rates slim Package or [] services must not wipe host rich values.
  preferRichPackageAndServices(merged, hostFlat);
  preferRichPackageAndServices(merged, formFlat);
  return merged;
};

/**
 * Host page (e.g. Add_shipment_onload) params/response + live form + click/scm params
 * for pages-config-execute / HyperThread.
 *
 * Callers (PagesRenderComponent / PagesAction) pass pageFlows + resolveHostConfig +
 * params (includes Plan C selected_rate from the rate-card click). Those must win
 * over host/form context so SelectedRateShipmentArrayMerge receives a Map.
 */
export const buildMergedPagesExecuteParams = ({
  inputParams = {},
  outputResponse = {},
  fieldFormPayload = {},
  hostPageParams = {},
  hostPageResponse = {},
  params = {},
  pageFlows,
  automationApiName,
  resolveHostConfig,
}) => {
  let resolvedHostParams = hostPageParams;
  let resolvedHostResponse = hostPageResponse;
  if (typeof resolveHostConfig === "function" && pageFlows) {
    const { pages: hostPages } = resolveHostConfig(
      pageFlows,
      automationApiName
    );
    if (hostPages) {
      resolvedHostParams = {
        ...(hostPages.params || {}),
        ...(hostPageParams || {}),
      };
      resolvedHostResponse = {
        ...(hostPages.response || {}),
        ...(hostPageResponse || {}),
      };
    }
  } else if (pageFlows) {
    const fromFlows = collectHostPageExecuteContext(pageFlows);
    resolvedHostParams = {
      ...fromFlows,
      ...(hostPageParams || {}),
    };
  }

  const base = buildPagesHostExecuteContext({
    inputParams,
    outputResponse,
    fieldFormPayload,
    hostPageParams: resolvedHostParams,
    hostPageResponse: resolvedHostResponse,
  });

  // Click / scm button params (selected_rate, etc.) must not be dropped.
  // Spread shipObj BEFORE flattening params: shipObj often still holds a stale
  // country code in from_postal_code ("CA") while the live form has the real
  // postal (e.g. N4S 7W5). Flattened form/click scalars must win.
  const paramsFlat = flattenPagesExecuteContext(params);
  const merged = {
    ...base,
    ...spreadShipObjContext(params),
    ...paramsFlat,
  };
  // Keep rich Package / selected services if params only had slim dims or [].
  preferRichPackageAndServices(merged, base);
  preferRichPackageAndServices(merged, paramsFlat);
  // Keep object values that flatten may skip when empty-string siblings exist;
  // selected_rate must remain the clicked Map for Plan C merge.
  if (
    params &&
    typeof params === "object" &&
    !Array.isArray(params) &&
    params.selected_rate != null &&
    typeof params.selected_rate === "object"
  ) {
    merged.selected_rate = params.selected_rate;
  }
  return merged;
};

/**
 * Build execute params for type="page" buttons.
 * Precedence (low → high): host context, carry-forward, loop row (unless
 * selected_rate), data-scm-* / resolved expressions.
 * Plan C: selected_rate = clicked card; shipment_array merge is backend-owned.
 */
export const buildPagesScmAutomationParams = ({
  attributes = {},
  loopData,
  dynamicVar,
  fieldFormPayload = {},
  inputParams = {},
  outputResponse = {},
  pageFlows,
}) => {
  const explicit = getDataScmAttributes(
    attributes,
    extractFieldValue,
    dynamicVar
  );
  // data-scm-selected_rate="${shipmentCharges}" often resolves to "" / "${…}"
  // before click; never let that overwrite the real rate-card Map.
  if (
    Object.prototype.hasOwnProperty.call(explicit, "selected_rate") &&
    isUnresolvedSelectedRatePlaceholder(explicit.selected_rate)
  ) {
    delete explicit.selected_rate;
  }

  let params = {};

  if (shouldCarryForwardPageContext(attributes)) {
    params = {
      ...collectHostPageExecuteContext(pageFlows),
      ...flattenPagesExecuteContext(outputResponse),
      ...flattenPagesExecuteContext(inputParams),
      ...flattenFormPayload(fieldFormPayload),
    };
  }
  if (isUnresolvedSelectedRatePlaceholder(params.selected_rate)) {
    delete params.selected_rate;
  }

  const loopIsRateCard = looksLikeSelectedRateCard(loopData);
  const passSelectedRate =
    shouldPassSelectedRateFromLoop(attributes) || loopIsRateCard;
  if (
    shouldMergeLoopIntoPageAutomationParams(attributes) &&
    loopData != null &&
    typeof loopData === "object" &&
    !Array.isArray(loopData)
  ) {
    if (passSelectedRate) {
      // Clicked rate card only — do not overlay form address/package/postal.
      params.selected_rate = loopData;
    } else {
      params = {
        ...params,
        ...loopData,
      };
    }
  }

  return {
    ...params,
    ...explicit,
    // Prefer whole loop card over unresolved ${…} / {{in}} placeholders.
    ...(passSelectedRate &&
    loopData != null &&
    typeof loopData === "object" &&
    !Array.isArray(loopData)
      ? { selected_rate: loopData }
      : {}),
  };
};
